import {
  ChangeEvent,
  Dispatch,
  lazy,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import GoogleMap from "components/googlemap/GoogleMap";
import Loading from "components/Loading";
import useCreateOption from "hooks/api/useCreateOption";
import useFindOrders from "hooks/api/useFindOrders";
import useGetOrder from "hooks/api/useGetOrder";
import { useAddressNormalization, NormalizedAddress } from "hooks/useAddressNormalization";
import { useDebounce } from "hooks/useDebounce";
import { useErrorModal } from "hooks/useModal";
import usePageTransition from "hooks/usePageTransition";
import { DATE_ASSIGNMENT, DateAssignmentType } from "pages/OptionCreate/constants";
import { INITIAL_REQUEST_SERVICES } from "pages/OptionCreate/constants/initialRequestServices";
import { INITIAL_CUSTOM_REQUEST_SERVICES } from "pages/OptionCreate/constants/initialUsingSpans";
import { RequestServiceInputType } from "pages/OptionCreate/type";
import { beautifyServices } from "pages/OptionCreate/utils/beautifyService";
import { today, tomorrow, isStartDateAfterEndDate } from "utils/dateUtils";
import { isBrowser, isTablet } from "utils/deviceDetect";

const OptionCreatePresenter = lazy(() =>
  isBrowser
    ? import("pages/OptionCreate/pc/OptionCreatePresenter")
    : isTablet
      ? import("pages/OptionCreate/sp/OptionCreatePresenter")
      : import("pages/OptionCreate/sp/OptionCreatePresenter")
);

export type UsingSpan = {
  id: string;
  usingStartDate?: string;
  usingEndDate?: string;
  isOneDay: boolean;
};

export type OptionCreatePresenterProps = {
  step: 1 | 2 | 3;
  handleStep: (step: 1 | 2 | 3) => void;
  onSubmit: () => void;
  navOptionList: () => void;
  navRelatedOrder: (id: string) => void;
  formValues: {
    globalUsingSpan: UsingSpan;
    orders: Order[] | undefined;
    name: string;
    searchActive: boolean;
    province: string;
    district: string;
    street: string;
    remarks: string;
    order?: { id: string; name: string };
    requestServices: RequestServiceInputType[];
    customServices: RequestServiceInputType[];
    handleChangeName: (name: string, orderId?: string) => void;
    handleCheckRequestServices: (content: string) => void;
    handleChangeProvince: (province: string) => void;
    handleChangeDistrict: (e: ChangeEvent<HTMLInputElement>) => void;
    handleChangeStreet: (e: ChangeEvent<HTMLInputElement>) => void;
    handleChangeRemarks: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    handleChangeOrder: (order: { id: string; name: string }) => void;
    handleChangeSearchActive: (active: boolean) => void;
    handleAddCustomRequestService: () => void;
    handleEditCustomRequestService: (id: string, content: string) => void;
    handleCheckCustomRequestService: (id: string) => void;
    handleChangeGlobalUsingSpan: (
      spanId: string,
      serviceId: string,
      date: string,
      type: "usingStartDate" | "usingEndDate"
    ) => void;
    handleChangeOneDayUse: (spanId: string, serviceId: string, isOneDay: boolean) => void;
    handleChangeRequestServicesUsingSpan: (
      spanId: string,
      serviceId: string,
      date: string,
      type: "usingStartDate" | "usingEndDate"
    ) => void;
    handleChangeRequestServicesOneDayUse: (spanId: string, serviceId: string, isOneDay: boolean) => void;
    handleAddRequestServiceUsingSpan: (serviceId: string, usingStartDate: string, usingEndDate: string) => void;
    handleChangeCustomRequestServiceUsingSpan: (
      spanId: string,
      serviceId: string,
      date: string,
      type: "usingStartDate" | "usingEndDate"
    ) => void;
    handleChangeCustomRequestServiceOneDayUse: (spanId: string, serviceId: string, isOneDay: boolean) => void;
    handleAddCustomRequestServiceUsingSpan: (serviceId: string, usingStartDate: string, usingEndDate: string) => void;
    errors: {
      isErrorName: boolean;
      isErrorRequestServices: boolean;
      isErrorProvince: boolean;
      isErrorDistrict: boolean;
      isErrorStreet: boolean;
      isErrorRemarks: boolean;
    };
    canSubmit: boolean;
    setOrdersPageIndex: Dispatch<SetStateAction<number>>;
    isLoadingOrders?: boolean;
    hasMoreOrders: boolean;
    dateAssignmentType: DateAssignmentType;
    handleChangeDateAssignmentType: (type: DateAssignmentType) => void;
    globalUsingSpansEditable?: boolean;
  };
  ErrorModal: JSX.Element;
};

const decodeQuery = decodeURI(window.location.search);
const urlParams = new URLSearchParams(decodeQuery);

const OptionCreateContainer = () => {
  const [globalUsingSpan, setGlobalUsingSpan] = useState<UsingSpan>({
    id: crypto.randomUUID(),
    isOneDay: false,
    usingStartDate: today,
    usingEndDate: tomorrow,
  });
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [dateAssignmentType, setDateAssignmentType] = useState<DateAssignmentType>(DATE_ASSIGNMENT.FIXED);
  const [name, setName] = useState(urlParams.get("constructionName") ?? "");
  const [requestServices, setRequestServices] = useState<RequestServiceInputType[]>(INITIAL_REQUEST_SERVICES("create"));
  const [customServices, setCustomServices] = useState<RequestServiceInputType[]>(INITIAL_CUSTOM_REQUEST_SERVICES);
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [street, setStreet] = useState("");
  const [remarks, setRemarks] = useState("");
  const [order, setOrder] = useState<{ id: string; name: string }>();
  const [searchActive, setSearchActive] = useState(false);
  const [kizukuSpotId] = useState<string | undefined>(urlParams.get("constructionCode") ?? undefined);
  const [ordersPageIndex, setOrdersPageIndex] = useState(1);
  const [errorModalState, setErrorModalState] = useState<{ httpMethod: HttpMethod; message?: string }>({
    httpMethod: "post",
    message: "",
  });

  const beautifyName = (name: string) => name.trim();
  const { debouncedValue: debouncedSearchKeyword } = useDebounce(name, 500);
  const { createOption } = useCreateOption();
  const navigate = usePageTransition();
  const { orders, isLoading, pagination } = useFindOrders({
    searchKeyword: beautifyName(debouncedSearchKeyword),
    orderStatusFilter: "all",
    orderType: "desc",
    pageNumber: ordersPageIndex,
    pageSize: 25,
    parkingUsageDateFilter: "notExpired",
  });
  const { trigger } = useGetOrder("");
  const { ErrorModal, showErrorModal } = useErrorModal();

  // 日付割り当て方法の変更
  const handleChangeDateAssignmentType = (type: DateAssignmentType) => {
    setDateAssignmentType(type);
  };

  const handleChangeStep = (step: 1 | 2 | 3) => {
    window.scrollTo(0, 0);
    setStep(step);
  };

  const handleChangeOrder = (order: { id: string; name: string }) => setOrder(order);

  const handleChangeName = async (name: string, orderId?: string) => {
    setName(name);
    if (!orderId) {
      setOrder(undefined);
      return;
    }
    setOrder({ id: orderId, name });
    await trigger(orderId);
  };

  // その他の申請のチェックボックス管理
  const handleCheckCustomRequestService = (serviceId: string) => {
    setCustomServices((prev) =>
      prev.map((service) =>
        service.id === serviceId ? { ...service, checked: !service.checked, editable: true } : service
      )
    );
  };

  // その他の申請追加
  const handleAddCustomRequestService = () => {
    setCustomServices((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        content: "",
        checked: true,
        editable: true,
        usingSpans: [
          {
            id: crypto.randomUUID(),
            usingStartDate: today,
            usingEndDate: tomorrow,
            isOneDay: false,
          },
        ],
      },
    ]);
  };

  // その他の申請の内容変更
  const handleEditCustomRequestService = (serviceId: string, content: string) => {
    setCustomServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              content,
              checked: true,
              editable: true,
            }
          : service
      )
    );
  };

  // その他申請の期間追加
  const handleAddCustomRequestServiceUsingSpan = (serviceId: string, usingStartDate: string, usingEndDate: string) => {
    setCustomServices((prev) => {
      return prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              usingSpans: [
                ...service.usingSpans,
                {
                  id: crypto.randomUUID(),
                  usingStartDate,
                  usingEndDate,
                  isOneDay: false,
                },
              ],
            }
          : service
      );
    });
  };

  // その他申請の期間変更
  const handleChangeCustomRequestServiceUsingSpan = (
    spanId: string,
    serviceId: string,
    date: string,
    type: "usingStartDate" | "usingEndDate"
  ) => {
    setCustomServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              usingSpans: service.usingSpans.map((span) =>
                span.id === spanId
                  ? {
                      ...span,
                      [type]: date,
                      ...(span.isOneDay && type === "usingStartDate" && { usingEndDate: date }), // isOneDayの場合、usingStartDateを変更するとusingEndDateも同じ値にする
                    }
                  : span
              ),
            }
          : service
      )
    );
  };

  // その他申請の1日利用の変更
  const handleChangeCustomRequestServiceOneDayUse = (spanId: string, serviceId: string, isOneDay: boolean) => {
    setCustomServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              usingSpans: service.usingSpans.map((span) =>
                span.id === spanId ? { ...span, usingEndDate: span.usingStartDate, isOneDay } : span
              ),
            }
          : service
      )
    );
  };

  // 全体期間の変更
  const handleChangeGlobalUsingSpan = (
    spanId: string,
    serviceId: string,
    date: string,
    type: "usingStartDate" | "usingEndDate"
  ) => {
    setGlobalUsingSpan((prev) => ({
      ...prev,
      [type]: date,
      ...(prev.isOneDay && type === "usingStartDate" && { usingEndDate: date }), // isOneDayの場合、usingStartDateを変更するとusingEndDateも同じ値にする
    }));
  };

  // 全体期間の1日利用の変更
  const handleChangeOneDayUse = (spanId: string, _serviceId: string, isOneDay: boolean) => {
    setGlobalUsingSpan((prev) => ({
      ...prev,
      usingEndDate: prev.usingStartDate,
      isOneDay,
    }));
  };

  // 申請サービスのチェックボックス管理
  const handleCheckRequestServices = (serviceId: string) => {
    const isChecked = requestServices.find((service) => service.id === serviceId)?.checked;

    // 既にチェックされている場合はチェックを外すして、期間を空にする
    // 既にチェックされていない場合はチェックをつけて、デフォルトの期間を設定する
    // サービスが存在しない場合はスキップ
    if (isChecked === undefined) return;

    if (!isChecked) {
      setRequestServices((prev) => {
        return prev.map((service) =>
          service.id === serviceId
            ? {
                ...service,
                checked: !service.checked,
                usingSpans: [
                  { id: crypto.randomUUID(), usingStartDate: today, usingEndDate: tomorrow, isOneDay: false },
                ],
              }
            : service
        );
      });
    } else {
      setRequestServices((prev) =>
        prev.map((service) =>
          service.id === serviceId ? { ...service, checked: !service.checked, usingSpans: [] } : service
        )
      );
    }
  };

  // 申請サービスの期間変更
  const handleChangeRequestServicesUsingSpan = (
    spanId: string,
    serviceId: string,
    date: string,
    type: "usingStartDate" | "usingEndDate"
  ) => {
    setRequestServices((prev) => {
      const findUsingSpan = prev
        .find((service) => service.id === serviceId)
        ?.usingSpans.find((span) => span.id === spanId);

      if (!findUsingSpan) return prev;

      const newUsingSpan = {
        ...findUsingSpan,
        [type]: date,
        ...(findUsingSpan.isOneDay && type === "usingStartDate" && { usingEndDate: date }), // isOneDayの場合、usingStartDateを変更するとusingEndDateも同じ値にする
      };
      return prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              usingSpans: [newUsingSpan],
            }
          : service
      );
    });
  };

  // 申請サービスの1日利用の変更
  const handleChangeRequestServicesOneDayUse = (spanId: string, serviceId: string, isOneDay: boolean) => {
    setRequestServices((prev) => {
      return prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              usingSpans: service.usingSpans.map((span) =>
                span.id === spanId ? { ...span, usingEndDate: span.usingStartDate, isOneDay } : span
              ),
            }
          : service
      );
    });
  };

  // 申請サービスの期間追加
  const handleAddRequestServiceUsingSpan = (serviceId: string, usingStartDate: string, usingEndDate: string) => {
    setRequestServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              usingSpans: [
                ...service.usingSpans,
                {
                  id: crypto.randomUUID(),
                  usingStartDate,
                  usingEndDate,
                  isOneDay: false,
                },
              ],
            }
          : service
      )
    );
  };

  const handleChangeDistrict = (e: ChangeEvent<HTMLInputElement>) => setDistrict(e.target.value);
  const handleChangeProvince = (province: string) => setProvince(province);
  const handleChangeStreet = (e: ChangeEvent<HTMLInputElement>) => setStreet(e.target.value);
  const handleChangeRemarks = (e: ChangeEvent<HTMLTextAreaElement>) => setRemarks(e.target.value);
  const handleChangeSearchActive = (active: boolean) => setSearchActive(active);

  const hasMoreOrders = pagination.totalPages > ordersPageIndex;
  const isMaxLengthError = (value: string, maxLength = 255) => value.trim().length > maxLength;
  const isErrorName = name.trim().length === 0;
  const isErrorRequestServices = (() => {
    const allServices = [...requestServices, ...customServices];
    // 1つもチェックされていない場合
    if (allServices.every((s) => !s.checked)) return true;
    // チェックされているサービスの中で、利用開始日 > 利用終了日 のものがある場合
    for (const service of allServices) {
      if (service.checked) {
        for (const span of service.usingSpans) {
          if (
            span.usingStartDate &&
            span.usingEndDate &&
            isStartDateAfterEndDate(span.usingStartDate, span.usingEndDate)
          ) {
            return true;
          }
        }
      }
    }
    return false;
  })();
  const isErrorAtLeastOneUsingSpan =
    dateAssignmentType === DATE_ASSIGNMENT.RESPECTIVE &&
    customServices.filter((s) => s.checked).some((service) => service.usingSpans.length === 0) &&
    requestServices.filter((s) => s.checked).some((service) => service.usingSpans.length === 0);
  const isErrorProvince = (district.length !== 0 || street.length !== 0) && province.length === 0;
  const isErrorDistrict = (province.length !== 0 || street.length !== 0) && district.length === 0;
  const isErrorStreet = (province.length !== 0 || district.length !== 0) && street.length === 0;
  const isErrorRemarks = isMaxLengthError(remarks);
  const isGlobalUsingSpansError =
    !globalUsingSpan.usingStartDate ||
    !globalUsingSpan.usingEndDate ||
    isStartDateAfterEndDate(globalUsingSpan.usingStartDate, globalUsingSpan.usingEndDate);

  const canSubmit =
    !isErrorName &&
    !isErrorRequestServices &&
    !isErrorProvince &&
    !isErrorDistrict &&
    !isErrorStreet &&
    !isErrorRemarks &&
    (dateAssignmentType === DATE_ASSIGNMENT.FIXED ? !isGlobalUsingSpansError : !isErrorAtLeastOneUsingSpan);

  const errors = {
    isErrorName,
    isErrorRequestServices,
    isErrorProvince,
    isErrorDistrict,
    isErrorStreet,
    isErrorRemarks,
  };
  const formValues = {
    globalUsingSpan,
    orders,
    searchActive,
    name,
    requestServices,
    province,
    district,
    street,
    remarks,
    order,
    customServices,
    handleChangeName,
    handleCheckRequestServices,
    handleChangeProvince,
    handleChangeDistrict,
    handleChangeStreet,
    handleChangeRemarks,
    handleChangeOrder,
    handleChangeSearchActive,
    errors,
    canSubmit,
    isLoadingOrders: isLoading,
    setOrdersPageIndex,
    hasMoreOrders,
    dateAssignmentType,
    handleChangeDateAssignmentType,
    handleAddCustomRequestService,
    handleEditCustomRequestService,
    handleCheckCustomRequestService,
    handleChangeGlobalUsingSpan,
    handleChangeOneDayUse,
    handleChangeRequestServicesUsingSpan,
    handleChangeRequestServicesOneDayUse,
    handleAddRequestServiceUsingSpan,
    handleChangeCustomRequestServiceUsingSpan,
    handleChangeCustomRequestServiceOneDayUse,
    handleAddCustomRequestServiceUsingSpan,
  };

  const navOptionList = () => navigate("/option");

  const navRelatedOrder = (id: string) => window.open(`/order/${id}`, "_blank");

  const onSubmit = async () => {
    const services = beautifyServices(
      [...requestServices, ...customServices],
      true,
      dateAssignmentType,
      globalUsingSpan
    );

    const status = await createOption({
      name,
      requestService: services,
      orderId: order?.id,
      province,
      district,
      street,
      remarks,
      kizukuSpotId,
    }).catch((e) => {
      if (e.response.status === 409) {
        setErrorModalState({
          httpMethod: "post",
          message: "既存の検索依頼と重複した名前の申請依頼です",
        });
      } else if (e.response.status === 428) {
        setErrorModalState({
          httpMethod: "post",
          message: "選択した検索依頼の駐車場貸出終了予定日を過ぎているため、申請できません",
        });
      } else {
        setErrorModalState({ httpMethod: "post" });
      }
      showErrorModal();
    });
    if (status === 201) setStep(3);
  };

  const { TemporaryHiddenGoogleMap, normalizeAddress } = useAddressNormalization();

  useEffect(() => {
    (async () => {
      const addressParam = urlParams.get("address");
      if (addressParam && !province && !district && !street) {
        const result = await normalizeAddress(addressParam);
        if (result) {
          setProvince(result.province ?? "");
          setDistrict(result.district ?? "");
          setStreet(result.street ?? "");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TemporaryHiddenGoogleMap />
      <Suspense fallback={<Loading />}>
        <OptionCreatePresenter
          formValues={formValues}
          step={step}
          handleStep={handleChangeStep}
          onSubmit={onSubmit}
          navOptionList={navOptionList}
          navRelatedOrder={navRelatedOrder}
          ErrorModal={<ErrorModal httpMethod={errorModalState.httpMethod} message={errorModalState.message} />}
        />
      </Suspense>
    </>
  );
};

export default OptionCreateContainer;
